import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

/**
 * Admin-only CRUD for teams: create a team, rename it, and pick its
 * members. Opened from the "Teams" button in the top bar (admins only).
 */
const TeamManagerModal = ({ employees, onClose, onChanged }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editMemberIds, setEditMemberIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.getTeams()
      .then((data) => { setTeams(data); if (onChanged) onChanged(data); })
      .catch(() => toast.error('Could not load teams.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      setCreating(true);
      await api.createTeam({ name: newName.trim(), member_ids: [] });
      setNewName('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not create team.');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (team) => {
    setEditingId(team.id);
    setEditName(team.name);
    setEditMemberIds(team.members.map((m) => m.mavis_user_id));
  };

  const toggleMember = (empId) => {
    setEditMemberIds((prev) => (prev.includes(empId) ? prev.filter((i) => i !== empId) : [...prev, empId]));
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) {
      toast.error('Team name cannot be blank.');
      return;
    }
    try {
      setSaving(true);
      await api.updateTeam(editingId, { name: editName.trim(), member_ids: editMemberIds });
      setEditingId(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not update team.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Delete this team? Tickets already assigned are unaffected.')) return;
    try {
      await api.deleteTeam(teamId);
      if (editingId === teamId) setEditingId(null);
      load();
    } catch (error) {
      toast.error('Could not delete team.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Teams</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <form onSubmit={handleCreate} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input
            className="form-input"
            placeholder="New team name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={creating}
          />
          <button type="submit" className="btn-primary" disabled={creating || !newName.trim()}>Add team</button>
        </form>

        {loading ? (
          <p>Loading...</p>
        ) : teams.length === 0 ? (
          <p style={{ color: '#aab7c4' }}>No teams yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {teams.map((team) => (
              <div
                key={team.id}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '0.75rem',
                }}
              >
                {editingId === team.id ? (
                  <>
                    <input
                      className="form-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: '0.5rem' }}>
                      {employees.map((emp) => (
                        <label
                          key={emp.mavis_user_id}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', padding: '0.2rem 0', cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            checked={editMemberIds.includes(emp.mavis_user_id)}
                            onChange={() => toggleMember(emp.mavis_user_id)}
                          />
                          {emp.display_name || emp.username}
                        </label>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button type="button" className="btn-primary" onClick={handleSaveEdit} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => setEditingId(null)} disabled={saving}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>{team.name}</strong>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button type="button" className="btn-secondary" onClick={() => startEdit(team)}>Edit</button>
                        <button type="button" className="btn-danger" onClick={() => handleDelete(team.id)}>Delete</button>
                      </div>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#aab7c4', margin: '0.3rem 0 0 0' }}>
                      {team.members.length ? team.members.map((m) => m.display_name || m.username).join(', ') : 'No members yet'}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagerModal;
